import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getSession } from '../../lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'edit', label: 'Edits' },
  { key: 'comment', label: 'Comments' },
  { key: 'vote', label: 'Votes' },
];

const AVATAR_COLORS = [
  'bg-teal-500', 'bg-indigo-500', 'bg-rose-500', 'bg-amber-500',
  'bg-emerald-500', 'bg-purple-500', 'bg-sky-500', 'bg-orange-500',
];

function getAvatarColor(userId) {
  if (!userId) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitial(name) {
  if (!name) return '?';
  return name.trim()[0].toUpperCase();
}

/**
 * Format a timestamp into a relative string (e.g., "2h ago", "yesterday").
 */
export function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;

  if (diffMs < 0) return 'just now';

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;

  return `${Math.floor(months / 12)}y ago`;
}

export default function ActivityLog({ isOpen, onClose, tripId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // Get auth headers by reading token from localStorage (fast) or getSession() (fallback)
  const getAuthHeaders = useCallback(async () => {
    const headers = { 'Content-Type': 'application/json' };
    try {
      const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
      if (storageKey) {
        const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
        if (stored?.access_token) {
          headers['Authorization'] = `Bearer ${stored.access_token}`;
          return headers;
        }
      }
      const session = await getSession();
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
    } catch { /* proceed without auth */ }
    return headers;
  }, []);

  useEffect(() => {
    if (!isOpen || !tripId) return;
    setLoading(true);

    (async () => {
      const headers = await getAuthHeaders();
      fetch(`${BACKEND_URL}/api/trips/${tripId}/activity-log`, {
        headers,
      })
        .then(res => res.ok ? res.json() : Promise.reject(res))
        .then(data => {
          setItems(Array.isArray(data) ? data : data.items || []);
        })
        .catch(err => {
          console.error('Failed to fetch activity log:', err);
          setItems([]);
        })
        .finally(() => setLoading(false));
    })();
  }, [isOpen, tripId, getAuthHeaders]);

  // Add new items from realtime
  const addRealtimeItem = useCallback((item) => {
    setItems(prev => {
      if (prev.some(i => i.id === item.id)) return prev;
      return [item, ...prev];
    });
  }, []);

  // Expose for parent wiring
  ActivityLog.addRealtimeItem = addRealtimeItem;

  const filteredItems = filter === 'all'
    ? items
    : items.filter(item => {
        const type = (item.action_type || item.type || '').toLowerCase();
        if (filter === 'edit') return type.includes('edit') || type.includes('add') || type.includes('remove') || type.includes('update');
        if (filter === 'comment') return type.includes('comment');
        if (filter === 'vote') return type.includes('vote');
        return true;
      });

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-[400px] max-w-[90vw] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Trip Activity</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 px-5 py-3 border-b border-gray-100">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors
                ${filter === tab.key
                  ? 'bg-teal-100 text-teal-700'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Feed */}
        <div className="overflow-y-auto h-[calc(100%-120px)] px-5 py-3">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex items-start gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0" />
                  <div className="flex-1">
                    <div className="h-3.5 w-3/4 bg-gray-200 rounded" />
                    <div className="h-3 w-16 bg-gray-100 rounded mt-1.5" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredItems.length === 0 ? (
            <p className="text-sm text-gray-400 text-center pt-8">
              {filter === 'all' ? 'No activity yet' : `No ${filter} activity`}
            </p>
          ) : (
            <div className="space-y-4">
              {filteredItems.map((item, idx) => (
                <FeedItem key={item.id || idx} item={item} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function FeedItem({ item }) {
  const userName = item.user_name || item.userName || 'Someone';
  const userId = item.user_id || item.userId;
  const actionText = item.description || item.action_text || buildActionText(item);
  const isComment = (item.action_type || item.type || '').toLowerCase().includes('comment');

  return (
    <div className="flex items-start gap-3">
      {/* Avatar */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${getAvatarColor(userId)}`}>
        {getInitial(userName)}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800">
          <span className="font-medium">{userName}</span>{' '}
          {actionText}
        </p>
        {isComment && item.comment_text && (
          <div className="mt-1 px-3 py-1.5 bg-gray-50 border-l-2 border-gray-300 rounded text-xs text-gray-600 italic truncate">
            "{item.comment_text}"
          </div>
        )}
        <p className="text-xs text-gray-400 mt-0.5">
          {formatRelativeTime(item.created_at || item.timestamp)}
        </p>
      </div>
    </div>
  );
}

function buildActionText(item) {
  const type = (item.action_type || item.type || '').toLowerCase();
  const target = item.target_name || item.targetName || '';

  if (type.includes('add') && type.includes('wishlist')) return `added ${target} to Wishlist`;
  if (type.includes('vote')) return `voted for ${target}`;
  if (type.includes('comment')) return `commented on ${target}`;
  if (type.includes('add')) return `added ${target}`;
  if (type.includes('remove')) return `removed ${target}`;
  if (type.includes('edit') || type.includes('update')) return `edited ${target}`;
  if (type.includes('join')) return 'joined the trip';

  return type || 'updated the trip';
}
